import { prisma } from "@/lib/db";
import { DEFAULTS } from "./constants";
import { calculatePCorrect } from "./predictor";
import type { CandidateState, GroupStatus, ProblemStatus } from "./types";

export interface GroupInfo {
  groupId: string;
  status: GroupStatus;
  totalProblems: number;
  introducedCount: number;
  stableCount: number;
  masteryScore: number;
}

/**
 * Check if a problem is "stable" for mastery calculation
 */
export function isStable(
  state: {
    consecutiveCorrect: number;
    memoryStrength: number;
    lastResult: string | null;
  },
  pCorrect: number
): boolean {
  return (
    state.consecutiveCorrect >= 2 &&
    state.memoryStrength >= DEFAULTS.stableMemoryStrength &&
    pCorrect >= DEFAULTS.stablePCorrect &&
    state.lastResult !== "wrong"
  );
}

/**
 * Calculate mastery score for a group
 */
export function calculateMasteryScore(
  introducedCount: number,
  stableCount: number,
  avgMemoryStrength: number
): number {
  if (introducedCount === 0) return 0;

  return (
    0.7 * (stableCount / introducedCount) + 0.3 * avgMemoryStrength
  );
}

/**
 * Check if group should be unlocked
 */
export function shouldUnlockNextGroup(
  totalProblems: number,
  introducedCount: number,
  stableCount: number,
  masteryScore: number
): boolean {
  if (introducedCount < totalProblems) return false;

  return (
    masteryScore >= DEFAULTS.groupUnlockMastery &&
    stableCount / totalProblems >= DEFAULTS.groupUnlockStableFraction
  );
}

/**
 * Get the active group for a user
 */
export async function getActiveGroup(userId: string): Promise<GroupInfo | null> {
  // First, try to find an active group
  const activeGroupState = await prisma.userGroupState.findFirst({
    where: {
      userId,
      status: "active",
    },
    include: {
      group: {
        include: {
          _count: {
            select: { problems: true },
          },
        },
      },
    },
  });

  if (activeGroupState) {
    return {
      groupId: activeGroupState.groupId,
      status: "active",
      totalProblems: activeGroupState.group._count.problems,
      introducedCount: activeGroupState.introducedCount,
      stableCount: activeGroupState.stableCount,
      masteryScore: activeGroupState.masteryScore,
    };
  }

  // If no active group, find the first locked group and activate it
  const groups = await prisma.problemGroup.findMany({
    orderBy: { sequence: "asc" },
    include: {
      _count: {
        select: { problems: true },
      },
    },
  });

  for (const group of groups) {
    const existingState = await prisma.userGroupState.findUnique({
      where: {
        userId_groupId: {
          userId,
          groupId: group.id,
        },
      },
    });

    if (!existingState) {
      // Create and activate this group
      await prisma.userGroupState.create({
        data: {
          userId,
          groupId: group.id,
          status: "active",
          unlockedAt: new Date(),
          introducedCount: 0,
          stableCount: 0,
          masteryScore: 0,
        },
      });

      return {
        groupId: group.id,
        status: "active",
        totalProblems: group._count.problems,
        introducedCount: 0,
        stableCount: 0,
        masteryScore: 0,
      };
    }

    if (existingState.status === "locked") {
      // Activate this locked group
      await prisma.userGroupState.update({
        where: {
          userId_groupId: {
            userId,
            groupId: group.id,
          },
        },
        data: {
          status: "active",
          unlockedAt: new Date(),
        },
      });

      return {
        groupId: group.id,
        status: "active",
        totalProblems: group._count.problems,
        introducedCount: existingState.introducedCount,
        stableCount: existingState.stableCount,
        masteryScore: existingState.masteryScore,
      };
    }
  }

  return null;
}

/**
 * Update group state after a problem answer
 */
export async function updateGroupStateAfterAnswer(
  userId: string,
  problemId: string,
  now: Date
): Promise<void> {
  const problem = await prisma.problem.findUnique({
    where: { id: problemId },
    select: { groupId: true },
  });

  if (!problem) return;

  const groupId = problem.groupId;

  // Get all problem states for this group
  const problemStates = await prisma.userProblemState.findMany({
    where: {
      userId,
      problem: {
        groupId,
      },
    },
  });

  // Count introduced and stable problems
  let introducedCount = 0;
  let stableCount = 0;
  let totalMemoryStrength = 0;

  for (const state of problemStates) {
    if (state.status !== "new") {
      introducedCount++;

      const candidateState: CandidateState = {
        status: state.status as ProblemStatus,
        memoryStrength: state.memoryStrength,
        difficulty: state.difficulty,
        consecutiveCorrect: state.consecutiveCorrect,
        consecutiveWrong: state.consecutiveWrong,
        lastResult: state.lastResult as "correct" | "wrong" | null,
        lastAnsweredAt: state.lastAnsweredAt,
        dueAt: state.dueAt,
        seenCountToday: state.seenCountToday,
        recentAvgResponseMs: state.recentAvgResponseMs,
        successCount: state.successCount,
      };

      const pCorrect = calculatePCorrect(candidateState, now);

      if (
        isStable(
          {
            consecutiveCorrect: state.consecutiveCorrect,
            memoryStrength: state.memoryStrength,
            lastResult: state.lastResult,
          },
          pCorrect
        )
      ) {
        stableCount++;
      }

      totalMemoryStrength += state.memoryStrength;
    }
  }

  const avgMemoryStrength =
    introducedCount > 0 ? totalMemoryStrength / introducedCount : 0;

  const masteryScore = calculateMasteryScore(
    introducedCount,
    stableCount,
    avgMemoryStrength
  );

  // Update group state
  await prisma.userGroupState.upsert({
    where: {
      userId_groupId: {
        userId,
        groupId,
      },
    },
    update: {
      introducedCount,
      stableCount,
      masteryScore,
    },
    create: {
      userId,
      groupId,
      status: "active",
      unlockedAt: now,
      introducedCount,
      stableCount,
      masteryScore,
    },
  });

  // Check if we should unlock the next group
  const totalProblems = await prisma.problem.count({
    where: { groupId },
  });

  if (shouldUnlockNextGroup(totalProblems, introducedCount, stableCount, masteryScore)) {
    // Mark current group as completed
    await prisma.userGroupState.update({
      where: {
        userId_groupId: {
          userId,
          groupId,
        },
      },
      data: {
        status: "completed",
        completedAt: now,
      },
    });

    // Find and activate next group
    const currentGroup = await prisma.problemGroup.findUnique({
      where: { id: groupId },
    });

    if (currentGroup) {
      const nextGroup = await prisma.problemGroup.findFirst({
        where: {
          sequence: { gt: currentGroup.sequence },
        },
        orderBy: { sequence: "asc" },
      });

      if (nextGroup) {
        await prisma.userGroupState.upsert({
          where: {
            userId_groupId: {
              userId,
              groupId: nextGroup.id,
            },
          },
          update: {
            status: "active",
            unlockedAt: now,
          },
          create: {
            userId,
            groupId: nextGroup.id,
            status: "active",
            unlockedAt: now,
            introducedCount: 0,
            stableCount: 0,
            masteryScore: 0,
          },
        });
      }
    }
  }
}
